/**
 * Miscellaneous pure functions to help when working w/ JavaScript/TypeScript arrays;
 *  - Array.prototype is a pain to work with and does NOT work reliably, especially w/ TypeScript
 *  - Array inheritance is a pain when working w/ TypeScript and is totally buggy, so pure functions are only solution
 *
 * NOTE: I was using Array.prototype in other TypeScript React projects, and it worked great, but when I copied
 *  exact same code in this project, it stopped working and always complained about 'function not found'. After
 *  trying to debug it, I figured out I'd go w/ a simpler, more reliable approach. Maybe it was caused by different
 *  ES target in tsconfig.json, but anyways it's already too unreliable.
 */


import {NestedFieldPaths} from "@ag-grid-community/core";
import {ICollection} from "../graphql/generated/graphql-main.tsx";

/**
 * Generic (higher order) function to find min value (numeric only) from array of objects using specified key.
 * Type 'T' defines the type of the array on which this reducer function is called
 * @param key
 */
export const minBy = <T>(key: keyof NonNullable<T>) => {
    // this is automatically called in loop manner, so accumulator gets updated from inside function body
    function reducer(accumulator: number, current: T) {
        // if no value found @ key, return the accumulator as is (which defaults to the initial value)
        if (current?.[key] == null) return accumulator;
        // as long as we find a smaller value, it becomes the new minimum
        const valAsNum = parseFloat(current?.[key] as string);
        if (valAsNum < accumulator) accumulator = valAsNum;
        return accumulator;
    }

    return reducer;
}

export const maxBy = <T>(key: keyof NonNullable<T>) => {
    // this is automatically called in loop manner, so accumulator gets updated from inside function body
    function reducer(accumulator: number, current: T) {
        // if no value found @ key, return the accumulator as is (which defaults to the initial value)
        if (current?.[key] == null) return accumulator;
        // as long as we find bigger value, it becomes the new maximum
        const valAsNum = parseFloat(current?.[key] as string);
        if (valAsNum > accumulator) accumulator = valAsNum;
        return accumulator;
    }

    return reducer;
}

/**
 * Todo consider using {@link NestedFieldPaths} for nested key
 * Given 1st-level or nested key, retrieve value at the specified key, using loop to navigate down the object
 * if we're passed a nested string key (using '.' to split into sub-keys).
 * https://dev.to/pffigueiredo/typescript-utility-keyof-nested-object-2pa3
 * @param obj
 * @param nestedKey
 */
// export const atKey = <T extends object, O>(obj: T, nestedKey: keyof T | string): O | null => {
export const atKey = <T extends object, O>(obj: T, nestedKey: NestedFieldPaths<T>): O | null => {
    // extract sub-keys if nested key
    const keys = (nestedKey as string).split('.');
    // dynamically loop down until we find the value at specified key
    let result: any = obj;
    for (const key of keys) {
        // result can be null, so use ?. to avoid fatal error trying to access [key]
        result = result?.[key as keyof T | string];
    }
    // return found value at nested key in expected 'O' format
    return result as O | null;
}

/**
 * Todo consider using {@link NestedFieldPaths} for nested key
 * Given array of T objects, use {@link Array.reduce} to create groups by key (optionally nested key).
 *
 * https://dmitripavlutin.com/javascript-array-group/
 * @param arr
 * @param key
 */
export type Grouping<T extends object> = { [key: string]: T[] }
// export const groupBy = <T extends object>(arr: T[], nestedKey: keyof T | string): Grouping<T> => {
export const groupBy = <T extends object>(arr: T[], nestedKey: NestedFieldPaths<T>): Grouping<T> => {
    return arr.reduce((group: Grouping<T>, entry: T) => {
        // try to get grouping key at specified key (maybe nested)
        const groupKey = atKey<T, string>(entry, nestedKey);
        // if value is not found, skip this iteration
        if (!groupKey)
            return group;

        // make sure sub-array exists at that key and push current entry in this array
        group[groupKey] ??= [];
        group[groupKey].push(entry);
        // return the group so next 'reduction' appends to this group based on grouping key
        return group;
    }, {});
}

/**
 * todo make more versatile by not assuming payload contains the 'id' value (add 'value' param)
 * Upsert entry in array using 'id' to either upsert if it's already there or push new entry.
 * Use {@link ICollection} as generic type to make this function usable with many subtypes
 * while still allowing type-checking for 'id' field in the function itself.
 * @param arr
 * @param payload
 */
export const upsertById = <T extends ICollection>(arr: T[], payload: T): void => {
    const _idx = findIndexBy<T>(arr, "id", payload.id);
    if (_idx === -1)
        arr.push(payload);
    else
        Object.assign(arr.at(_idx)!, payload);
}

/**
 *
 * @param arr - original array in which to upsert the payload
 * @param key - property of object type by which to search for needle
 * @param needle - actual property value that determines if item exists in array
 * @param payload - object to upsert by either merging w/ existing entry based on key+needle or create new entry
 */
export const upsertBy = <T>(arr: T[], key: keyof NonNullable<T>, needle: any, payload: T): void => {
    const _idx = findIndexBy<T>(arr, key, needle);
    if (_idx === -1)
        arr.push(payload);
    else
        Object.assign(arr.at(_idx)!, payload);
}

/**
 * Given specific array index, upsert object at that index or create entry if not already there
 * @param arr
 * @param idx
 * @param payload
 */
export const upsertAt = <T>(arr: Array<T>, idx: number, payload: T): void => {
    if (!arr[idx])
        arr[idx] = {} as any;
    Object.assign(arr.at(idx)!, payload);
}

export const filterBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, value: any, strict: boolean = false): T[] =>
    (strict
        ? arr?.filter((o) => o?.[key] === value)
        : arr?.filter((o) => o?.[key] == value));

export const maybeFilterBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, value: any, strict: boolean = false): T[] =>
    (strict
        ? arr?.filter((o) => value === null ? true : o?.[key] === value)
        : arr?.filter((o) => value == null ? true : o?.[key] == value));

export const filterByIn = <T>(arr: Array<T>, key: keyof NonNullable<T>, value: any[]): T[] =>
    arr?.filter((o) => value.includes(o?.[key]));

export const findLastBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, value: any, strict: boolean = false): T | null =>
    (strict
        ? arr.findLast((o) => o?.[key] === value)
        : arr.findLast((o) => o?.[key] == value)) ?? null;

export const findBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, value: any, strict: boolean = false): T | null =>
    (strict
        ? arr.find((o) => o?.[key] === value)
        : arr.find((o) => o?.[key] == value)) ?? null;

/**
 *
 * @param arr
 * @param key
 * @param haystack
 */
export const findManyBy = <T>(
    arr: Array<T>,
    key: keyof NonNullable<T>,
    haystack: any[],
): T[] =>
    arr.filter((o) => haystack.includes(o?.[key]));

/**
 *
 * @param arr
 * @param value
 * @param strict
 */
export const findByVal = <T>(arr: Array<T>, value: any, strict: boolean = false): T | null =>
    (strict
        ? arr.find((o) => o === value)
        : arr.find((o) => o == value)) ?? null;

/**
 *
 * @param arr
 * @param key
 * @param value
 * @param strict
 */
export const findIndexBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, value: any, strict: boolean = false) =>
    strict
        ? arr.findIndex((o) => o?.[key] === value)
        : arr.findIndex((o) => o?.[key] == value);

/**
 *
 * @param arr
 * @param start
 * @param count
 */
export const deleteAt = <T>(arr: Array<T>, start: number, count: number): T[] => {
    return arr.splice(start, count)
}

/**
 *
 * @param arr
 * @param key
 * @param value
 */
export const deleteBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, value: any): T[] => {
    const _idx = findIndexBy(arr, key, value)
    if (_idx !== -1)
        arr.splice(_idx, 1)
    return arr
};

/**
 *
 * @param arr
 * @param value
 * @param strict
 */
export const deleteValue = <T>(arr: Array<T>, value: any, strict: boolean = false): T[] => {
    const _idx = strict
        ? arr.findIndex(o => o === value)
        : arr.findIndex(o => o == value);
    if (_idx !== -1)
        arr.splice(_idx, 1);
    return arr;
};

/**
 * Generic compare function usable in both {@link Array.sort} and {@link Array.toSorted} functions.
 * It receives key & asc|desc as params and returns a function in the specific format expected by those
 * sort functions, thus allowing great flexibility in sorting anything anywhere.
 * @source
 *  - https://stackoverflow.com/questions/14491695/how-can-i-pass-more-parameters-to-a-custom-sort-function#answer-14491740
 * @param key
 * @param asc
 * @param asNumeric
 */
export const compareByKey = <T>(
    key: keyof NonNullable<T>,
    asc: boolean = true,
    asNumeric: boolean = false
) => {
    // return the actual sort function which matches exact signature as expected in `.sort` and `.toSorted`
    return function (a: T, b: T): number {
        let a_key = a?.[key] as string | number | null;
        let b_key = b?.[key] as string | number | null;

        // caution: when sorting numbers, !0 always equals 'true', so check specifically for null|undefined
        // note: in TS, == null implicitly also checks for == undefined, so NO need to check for both
        if (a_key == null || b_key == null)
            return 0;

        if (asNumeric) {
            a_key = parseInt(a_key as string) || a_key;
            b_key = parseInt(b_key as string) || b_key;
        }

        if (a_key < b_key)
            return asc ? -1 : 1;
        if (a_key > b_key)
            return asc ? 1 : -1;
        return 0;
    }
};

// allow comparing by nested key (todo merge w/ compareByKey since almost structure is generic to simple & nested cases)
export const compareByNestedKey = <T extends object>(args: {
    key: NestedFieldPaths<T>,
    asc: boolean,
    asNumeric: boolean
}) => {
    args.asc = args.asc ?? true;
    args.asNumeric = args.asNumeric ?? false;

    // return the actual sort function which matches exact signature as expected in `.sort` and `.toSorted`
    return function (a: T, b: T) {
        let a_key = atKey<T, string | number | null>(a, args.key);
        let b_key = atKey<T, string | number | null>(b, args.key);

        // note: in TS, == null implicitly also checks for == undefined, so NO need to check for both
        // caution: when sorting numbers, !0 always equals 'true', so check specifically for null|undefined
        if (a_key == null || b_key == null)
            return 0;

        // if we have pre-knowledge that specific 'string' field must be sorted numerically, do parseInt
        if (args.asNumeric) {
            a_key = parseFloat(a_key as string) || a_key;
            b_key = parseFloat(b_key as string) || b_key;
        }

        if (a_key < b_key)
            return args.asc ? -1 : 1;
        if (a_key > b_key)
            return args.asc ? 1 : -1;
        return 0;
    }
};

export const sortBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, asc: boolean = true): T[] => {
    return arr.sort(compareByKey(key, asc));
}

export const sortedBy = <T>(arr: Array<T>, key: keyof NonNullable<T>, asc: boolean = true): T[] => {
    return arr.toSorted(compareByKey(key, asc));
}

/**
 * Sort array of objects by multiple keys w/ individual ordering per key.
 * @param arr - array to be sorted
 * @param keys - key:asc array of keys to sort by and whether asc/desc per key
 */
export const sortedByMany = <T>(
    arr: Array<T>,
    keys: { key: keyof NonNullable<T>, asc: boolean, asNumeric: boolean }[]
): T[] => {

    const multiCompare = (a: T, b: T) => {
        for (const comparator of keys.values()) {
            const comparison = compareByKey(comparator.key, comparator.asc, comparator.asNumeric)(a, b);
            if (comparison == 0)
                continue;
            else
                return comparison;
        }
        return 0;
    }
    return arr.toSorted(multiCompare);
};


/**
 * https://medium.com/@alvaro.saburido/set-theory-for-arrays-in-es6-eb2f20a61848
 * @param arrA
 * @param arrB
 * @return Array<T>
 */
export const arrayIntersect = <T>(arrA: Array<T>, arrB: Array<T>): T[] => {
    console.info(`intersect.A`, arrA);
    console.info(`intersect.B`, arrB);
    return arrA.filter(x => arrB.includes(x))
};